import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Loading,
  Modal,
  Pagination,
  Select,
  StatusBadge,
  Table,
  Td,
  Th,
  Textarea,
  Tooltip,
  EmailStatusBadge,
  type SortDir,
} from '../components/ui';
import {
  api,
  apiUrl,
  type Campaign,
  type LifecycleStage,
  type Prospect,
  type QualifiedCandidate,
} from '../lib/api';
import { humanize, money, relative } from '../lib/format';
import { useAction, useApi, useDebounced } from '../lib/hooks';

const LIFECYCLE_STAGES: LifecycleStage[] = ['lead', 'engaged', 'qualified', 'customer', 'churned', 'disqualified'];
const LIFECYCLE_TONE: Record<LifecycleStage, 'neutral' | 'blue' | 'purple' | 'green' | 'amber'> = {
  lead: 'neutral',
  engaged: 'blue',
  qualified: 'purple',
  customer: 'green',
  churned: 'amber',
  disqualified: 'amber',
};

const PAGE_SIZE = 50;

/** Sort keys accepted by `GET /api/prospects` (`PROSPECT_SORTS` in routes/prospects.ts). */
type ProspectSort =
  | 'created'
  | 'name'
  | 'company'
  | 'campaign'
  | 'status'
  | 'lifecycle'
  | 'touches'
  | 'value'
  | 'last_contact';

/** First click on a column sorts in its natural direction: A→Z for text, biggest/most recent first for numbers and dates. */
const DEFAULT_DIR: Record<ProspectSort, SortDir> = {
  created: 'desc',
  name: 'asc',
  company: 'asc',
  campaign: 'asc',
  status: 'asc',
  lifecycle: 'asc',
  touches: 'desc',
  value: 'desc',
  last_contact: 'desc',
};

export function Prospects() {
  const [campaignId, setCampaignId] = useState('');
  const [status, setStatus] = useState('');
  const [lifecycleStage, setLifecycleStage] = useState('');
  const [tag, setTag] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState<{ key: ProspectSort; dir: SortDir }>({ key: 'created', dir: 'desc' });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showImport, setShowImport] = useState(false);
  const [showLinkedIn, setShowLinkedIn] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [gapBusy, setGapBusy] = useState(false);
  const [gapResult, setGapResult] = useState<string | null>(null);

  const debouncedSearch = useDebounced(search);
  const debouncedTag = useDebounced(tag);

  // Any filter change invalidates the current page — a filtered set of 12
  // does not have an "offset 150" to show.
  useEffect(() => {
    setOffset(0);
  }, [campaignId, status, lifecycleStage, debouncedTag, debouncedSearch, sort]);

  // Click a header to sort by it; click it again to reverse. Sorting runs on
  // the server because the list is paged there.
  function sortBy(key: ProspectSort) {
    return {
      dir: sort.key === key ? sort.dir : null,
      onSort: () =>
        setSort((current) =>
          current.key === key
            ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: DEFAULT_DIR[key] },
        ),
    };
  }

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (campaignId) params.set('campaign_id', campaignId);
    if (status) params.set('status', status);
    if (lifecycleStage) params.set('lifecycle_stage', lifecycleStage);
    if (debouncedTag) params.set('tag', debouncedTag);
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (sort.key !== 'created' || sort.dir !== 'desc') {
      params.set('sort', sort.key);
      params.set('dir', sort.dir);
    }
    return `/api/prospects?${params.toString()}`;
  }, [campaignId, status, lifecycleStage, debouncedTag, debouncedSearch, offset, sort]);

  const prospects = useApi<{ prospects: Prospect[]; total: number }>(path);
  const campaigns = useApi<{ campaigns: Campaign[] }>('/api/campaigns');

  const rows = prospects.data?.prospects ?? [];
  /**
   * The coverage gap measures a website, so it is stored on the ORGANIZATION,
   * not on each contact at it. The server resolves the selected prospects to
   * their distinct companies before fetching, so selecting eight people at one
   * nonprofit costs one fetch and writes one row. Triggered from here only
   * because this is where a list is actually worked — there is no Companies
   * page yet.
   */
  async function scoreGap() {
    setGapBusy(true);
    setGapResult(null);
    try {
      const response = await api.post<{
        scored: number;
        skipped_no_domain: string[];
        by_tier: Record<string, number>;
      }>('/api/companies/score-coverage-gap', { prospect_ids: [...selected] });

      const tiers = Object.entries(response.by_tier)
        .sort((a, b) => b[1] - a[1])
        .map(([tier, count]) => `${count} ${tier}`)
        .join(', ');
      const skipped = response.skipped_no_domain.length
        ? `; ${response.skipped_no_domain.length} skipped with no website`
        : '';

      setGapResult(
        `Scored ${response.scored} organization${response.scored === 1 ? '' : 's'}` +
          `${tiers ? ` — ${tiers}` : ''}${skipped}. ` +
          'Counts come from each sitemap, so they are a floor: most sitemaps omit ' +
          'PDFs entirely, and documents held in portals or handed out on paper are ' +
          'never visible.',
      );
      prospects.reload();
    } catch (error) {
      setGapResult(error instanceof Error ? error.message : 'Scoring failed');
    } finally {
      setGapBusy(false);
    }
  }

  const allSelected = rows.length > 0 && rows.every((row) => selected.has(row.id));

  function toggle(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <>
      <PageHeader
        title="Prospects"
        description={`${prospects.data?.total ?? 0} in view`}
        helpHref="prospects"
        actions={
          <>
            <a
              href={apiUrl(`/api/prospects/export/csv${campaignId ? `?campaign_id=${campaignId}` : ''}`)}
              className="inline-flex items-center rounded-md bg-white px-3.5 py-2 text-sm font-medium text-ink-800 ring-1 ring-inset ring-ink-200 hover:bg-ink-50"
            >
              Export CSV
            </a>
            <Button onClick={() => setShowImport(true)}>Import CSV</Button>
            <Button onClick={() => setShowLinkedIn(true)}>Import from LinkedIn</Button>
            <Button variant="primary" onClick={() => setShowNew(true)}>
              Add prospect
            </Button>
          </>
        }
      />

      <Card className="mb-4" bodyClassName="p-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[16rem] flex-1">
            <Field label="Search">
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Name, email, or company"
              />
            </Field>
          </div>
          <div className="w-56">
            <Field label="Campaign">
              <Select value={campaignId} onChange={(event) => setCampaignId(event.target.value)}>
                <option value="">All campaigns</option>
                {campaigns.data?.campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="w-44">
            <Field label="Status">
              <Select value={status} onChange={(event) => setStatus(event.target.value)}>
                <option value="">Active</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="replied">Replied</option>
                <option value="won">Won</option>
                <option value="lost">Lost</option>
                <option value="archived">Archived</option>
              </Select>
            </Field>
          </div>
          <div className="w-44">
            <Field label="Lifecycle stage">
              <Select value={lifecycleStage} onChange={(event) => setLifecycleStage(event.target.value)}>
                <option value="">All stages</option>
                {LIFECYCLE_STAGES.map((stageOption) => (
                  <option key={stageOption} value={stageOption}>
                    {humanize(stageOption)}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="w-36">
            <Field label="Tag">
              <Input value={tag} onChange={(event) => setTag(event.target.value)} placeholder="e.g. vip" />
            </Field>
          </div>
          {selected.size > 0 && (
            <>
              <Button variant="primary" onClick={() => setShowBulk(true)}>
                Draft outreach for {selected.size}
              </Button>
              <Button onClick={scoreGap} disabled={gapBusy}>
                {gapBusy ? 'Scoring…' : 'Score coverage gap'}
              </Button>
            </>
          )}
        </div>
        {gapResult && (
          <p className="mt-3 text-sm text-ink-600">
            {gapResult}{' '}
            <button
              type="button"
              className="underline"
              onClick={() => setGapResult(null)}
            >
              dismiss
            </button>
          </p>
        )}
      </Card>

      <Card bodyClassName="p-0">
        {prospects.loading && <Loading />}
        {prospects.error && <ErrorNote message={prospects.error} onRetry={prospects.reload} />}
        {!prospects.loading && rows.length === 0 && (
          <EmptyState
            title="No prospects here"
            description="Import a CSV from LinkedIn or your list provider, or add someone manually."
            action={<Button variant="primary" onClick={() => setShowImport(true)}>Import CSV</Button>}
          />
        )}

        {rows.length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th className="w-8">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={allSelected}
                    onChange={() =>
                      setSelected(allSelected ? new Set() : new Set(rows.map((row) => row.id)))
                    }
                    className="rounded border-ink-300"
                  />
                </Th>
                <Th sort={sortBy('name')}>Prospect</Th>
                <Th sort={sortBy('company')}>Company</Th>
                <Th sort={sortBy('campaign')}>Campaign</Th>
                <Th sort={sortBy('status')}>Status</Th>
                <Th sort={sortBy('lifecycle')}>Lifecycle</Th>
                <Th sort={sortBy('touches')} tip="Number of outreach messages sent to this prospect across all channels.">Touches</Th>
                <Th sort={sortBy('value')} tip="Estimated deal value, if one has been set on this prospect.">Value</Th>
                <Th sort={sortBy('last_contact')}>Last contact</Th>
                <Th className="w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {rows.map((prospect) => (
                <tr key={prospect.id} className="hover:bg-ink-50/60">
                  <Td>
                    <input
                      type="checkbox"
                      aria-label={`Select ${prospect.first_name}`}
                      checked={selected.has(prospect.id)}
                      onChange={() => toggle(prospect.id)}
                      className="rounded border-ink-300"
                    />
                  </Td>
                  <Td>
                    <Link
                      to={`/prospects/${prospect.id}`}
                      className="font-medium text-ink-900 hover:text-accent-700"
                    >
                      {`${prospect.first_name} ${prospect.last_name}`.trim() || 'Unnamed'}
                    </Link>
                    <div className="flex items-center gap-1.5 text-xs text-ink-500">
                      <span>{prospect.job_title || prospect.email}</span>
                      <EmailStatusBadge status={prospect.email_status} />
                    </div>
                  </Td>
                  <Td>
                    <div>{prospect.company_name || '—'}</div>
                    {prospect.company_industry && (
                      <div className="text-xs text-ink-500">{prospect.company_industry}</div>
                    )}
                  </Td>
                  <Td>
                    <div className="text-sm">{prospect.campaign_name ?? '—'}</div>
                    {prospect.persona_name && (
                      <div className="text-xs text-ink-500">{prospect.persona_name}</div>
                    )}
                  </Td>
                  <Td>
                    <StatusBadge status={prospect.status} />
                  </Td>
                  <Td>
                    <Badge tone={LIFECYCLE_TONE[prospect.lifecycle_stage]}>
                      {humanize(prospect.lifecycle_stage)}
                    </Badge>
                  </Td>
                  <Td>
                    <span className="tabular-nums">{prospect.touch_count ?? 0}</span>
                    {(prospect.reply_count ?? 0) > 0 && (
                      <Badge tone="purple">{prospect.reply_count} replied</Badge>
                    )}
                  </Td>
                  <Td className="tabular-nums">{money(prospect.deal_value)}</Td>
                  <Td className="text-xs text-ink-500">{relative(prospect.last_contacted_at)}</Td>
                  <Td>
                    <ApolloEnrichButton prospect={prospect} onEnriched={prospects.reload} />
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {!prospects.loading && rows.length > 0 && (
          <Pagination
            offset={offset}
            limit={PAGE_SIZE}
            total={prospects.data?.total ?? rows.length}
            onOffsetChange={setOffset}
          />
        )}
      </Card>

      <ImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        campaigns={campaigns.data?.campaigns ?? []}
        onDone={() => {
          setShowImport(false);
          prospects.reload();
        }}
      />

      <LinkedInQualifyModal
        open={showLinkedIn}
        onClose={() => setShowLinkedIn(false)}
        campaigns={campaigns.data?.campaigns ?? []}
        onDone={() => {
          setShowLinkedIn(false);
          prospects.reload();
        }}
      />

      <NewProspectModal
        open={showNew}
        onClose={() => setShowNew(false)}
        campaigns={campaigns.data?.campaigns ?? []}
        onDone={() => {
          setShowNew(false);
          prospects.reload();
        }}
      />

      <BulkDraftModal
        open={showBulk}
        onClose={() => setShowBulk(false)}
        prospectIds={[...selected]}
        onDone={() => {
          setShowBulk(false);
          setSelected(new Set());
        }}
      />
    </>
  );
}

function ApolloEnrichButton({ prospect, onEnriched }: { prospect: Prospect; onEnriched: () => void }) {
  const enrich = useAction(async () => {
    await api.post(`/api/prospects/${prospect.id}/enrich-from-apollo`);
    onEnriched();
  });

  return (
    <div>
      <Tooltip content="Pulls fresh company and title data from Apollo into this prospect's fields. Read-only — nothing here is ever sent to Apollo.">
        <Button
          size="sm"
          variant="ghost"
          loading={enrich.pending}
          onClick={() => void enrich.run()}
          disabled={!prospect.email}
        >
          Enrich
        </Button>
      </Tooltip>
      {enrich.error && <p className="mt-1 max-w-[10rem] text-xs text-red-700">{enrich.error}</p>}
    </div>
  );
}

function ImportModal({
  open,
  onClose,
  campaigns,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  campaigns: Campaign[];
  onDone: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [campaignId, setCampaignId] = useState('');
  const [result, setResult] = useState<{ imported: number; skipped: number; errors: string[] } | null>(null);

  const submit = useAction(async () => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    if (campaignId) form.append('campaign_id', campaignId);
    const response = await api.post<{ imported: number; skipped: number; errors: string[] }>(
      '/api/prospects/import',
      form,
    );
    setResult(response);
    if (response.imported > 0) onDone();
  });

  return (
    <Modal open={open} onClose={onClose} title="Import prospects from CSV">
      <div className="space-y-4">
        <p className="text-sm text-ink-600">
          Columns are matched loosely — <span className="font-mono text-xs">First Name</span>,{' '}
          <span className="font-mono text-xs">Email</span>, <span className="font-mono text-xs">Company</span>,{' '}
          <span className="font-mono text-xs">Title</span>, <span className="font-mono text-xs">LinkedIn URL</span>{' '}
          all work. Anything unrecognised is kept as a custom field, and a row needs at least an email
          or a LinkedIn URL.
        </p>

        <Field label="CSV file">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            className="block w-full text-sm text-ink-700 file:mr-3 file:rounded-md file:border-0 file:bg-ink-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-ink-800 hover:file:bg-ink-200"
          />
        </Field>

        <Field label="Assign to campaign" hint="Personas are matched automatically by job title.">
          <Select value={campaignId} onChange={(event) => setCampaignId(event.target.value)}>
            <option value="">No campaign</option>
            {campaigns.map((campaign) => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.name}
              </option>
            ))}
          </Select>
        </Field>

        {submit.error && <ErrorNote message={submit.error} />}

        {result && (
          <div className="rounded-md bg-ink-50 p-3 text-sm">
            <p className="font-medium text-ink-900">
              {result.imported} imported, {result.skipped} skipped
            </p>
            {result.errors.length > 0 && (
              <ul className="mt-2 max-h-40 space-y-0.5 overflow-y-auto text-xs text-ink-600">
                {result.errors.slice(0, 20).map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Close</Button>
          <Button variant="primary" loading={submit.pending} disabled={!file} onClick={() => void submit.run()}>
            Import
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const VERDICT_TONES = { strong_fit: 'green', possible_fit: 'amber', poor_fit: 'red' } as const;
const VERDICT_LABELS = { strong_fit: 'strong fit', possible_fit: 'possible', poor_fit: 'poor fit' } as const;

function LinkedInQualifyModal({
  open,
  onClose,
  campaigns,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  campaigns: Campaign[];
  onDone: () => void;
}) {
  const [campaignId, setCampaignId] = useState('');
  // The default criteria follow the chosen campaign's ICP, so they are re-fetched on a change.
  const defaults = useApi<{ criteria: string }>(
    `/api/prospects/qualify/defaults${campaignId ? `?campaign_id=${campaignId}` : ''}`,
  );
  const [text, setText] = useState('');
  const [criteria, setCriteria] = useState('');
  const [criteriaEdited, setCriteriaEdited] = useState(false);
  const [showCriteria, setShowCriteria] = useState(false);
  const [targetCount, setTargetCount] = useState('10');
  const [candidates, setCandidates] = useState<QualifiedCandidate[] | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [result, setResult] = useState<{ imported: number; skipped: number; errors: string[] } | null>(null);

  useEffect(() => {
    if (defaults.data && !criteriaEdited) setCriteria(defaults.data.criteria);
    // Seeds the editable criteria when a default arrives, including a new campaign's; hand edits win.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaults.data]);

  const score = useAction(async () => {
    const response = await api.post<{ candidates: QualifiedCandidate[] }>('/api/prospects/qualify', {
      text,
      criteria: criteria || undefined,
      campaign_id: campaignId || null,
      target_count: Number(targetCount) || 10,
    });
    setCandidates(response.candidates);
    setPicked(
      new Set(
        response.candidates
          .map((candidate, index) => (candidate.verdict === 'strong_fit' && !candidate.already_in_crm ? index : -1))
          .filter((index) => index >= 0),
      ),
    );
  });

  const doImport = useAction(async () => {
    const chosen = (candidates ?? []).filter((_, index) => picked.has(index));
    const response = await api.post<{ imported: number; skipped: number; errors: string[] }>(
      '/api/prospects/qualify/import',
      {
        campaign_id: campaignId || null,
        candidates: chosen.map(({ verdict: _v, already_in_crm: _a, ...rest }) => rest),
      },
    );
    setResult(response);
  });

  function togglePick(index: number) {
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  return (
    <Modal open={open} onClose={onClose} title="Import from LinkedIn" width="max-w-3xl">
      {!candidates && (
        <div className="space-y-4">
          <p className="text-sm text-ink-600">
            Run your search in LinkedIn or Sales Navigator, select the results text on the page
            (Ctrl/Cmd-A works), copy, and paste it below — several pages in a row is fine. Claude
            extracts the people and scores each one against your ideal-customer criteria; you then
            pick which to import. Nothing is saved until you confirm.
          </p>

          <Field label="Pasted search results">
            <Textarea
              rows={8}
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="Paste the copied Sales Navigator results here…"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="How many good prospects do you want?">
              <Input
                type="number"
                min={1}
                max={25}
                value={targetCount}
                onChange={(event) => setTargetCount(event.target.value)}
              />
            </Field>
            <Field label="Assign to campaign" hint="Its context is added to the scoring criteria.">
              <Select value={campaignId} onChange={(event) => setCampaignId(event.target.value)}>
                <option value="">No campaign</option>
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <div>
            <button
              type="button"
              className="text-sm font-medium text-accent-700 hover:text-accent-800"
              onClick={() => setShowCriteria((current) => !current)}
            >
              {showCriteria ? 'Hide scoring criteria' : 'Edit scoring criteria'}
            </button>
            {showCriteria && (
              <div className="mt-2">
                <Field
                  label="What makes a good prospect"
                  hint="Claude scores every pasted person against this. Edit freely — changes apply to this run only."
                >
                  <Textarea
                    rows={10}
                    value={criteria}
                    onChange={(event) => {
                      setCriteria(event.target.value);
                      setCriteriaEdited(true);
                    }}
                  />
                </Field>
              </div>
            )}
          </div>

          {score.error && <ErrorNote message={score.error} />}

          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              loading={score.pending}
              disabled={text.trim().length < 20}
              onClick={() => void score.run()}
            >
              Score with Claude
            </Button>
          </div>
        </div>
      )}

      {candidates && (
        <div className="space-y-4">
          <p className="text-sm text-ink-600">
            {candidates.length} people found — strong fits are pre-selected. Untick anyone you don't
            want; people already in the CRM can't be imported twice.
          </p>

          <div className="max-h-[26rem] space-y-2 overflow-y-auto pr-1">
            {candidates.map((candidate, index) => {
              const name = `${candidate.first_name} ${candidate.last_name}`.trim() || 'Unnamed';
              return (
                <label
                  key={index}
                  className={`flex cursor-pointer items-start gap-3 rounded-md border border-ink-200 p-3 ${
                    candidate.already_in_crm ? 'opacity-60' : 'hover:bg-ink-50/60'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mt-1 rounded border-ink-300"
                    checked={picked.has(index)}
                    disabled={candidate.already_in_crm}
                    onChange={() => togglePick(index)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-ink-900">{name}</span>
                      <Badge
                        tone={VERDICT_TONES[candidate.verdict]}
                        title="Claude's 0–100 fit score against your ICP criteria, and its verdict from that score."
                      >
                        {Math.round(candidate.fit_score)} · {VERDICT_LABELS[candidate.verdict]}
                      </Badge>
                      {candidate.already_in_crm && <Badge tone="purple">already in CRM</Badge>}
                      {candidate.linkedin_url && (
                        <a
                          href={candidate.linkedin_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-accent-700 hover:underline"
                          onClick={(event) => event.stopPropagation()}
                        >
                          profile ↗
                        </a>
                      )}
                    </div>
                    <div className="text-sm text-ink-700">
                      {[candidate.job_title, candidate.company_name].filter(Boolean).join(' · ') || '—'}
                      {candidate.location ? ` · ${candidate.location}` : ''}
                    </div>
                    {candidate.fit_reasons.length > 0 && (
                      <ul className="mt-1 space-y-0.5 text-xs text-ink-600">
                        {candidate.fit_reasons.map((reason) => (
                          <li key={reason}>• {reason}</li>
                        ))}
                      </ul>
                    )}
                    {candidate.matched_keywords.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {candidate.matched_keywords.map((keyword) => (
                          <Badge key={keyword} tone="blue">
                            {keyword}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>

          {doImport.error && <ErrorNote message={doImport.error} />}

          {result && (
            <div className="rounded-md bg-ink-50 p-3 text-sm">
              <p className="font-medium text-ink-900">
                {result.imported} imported, {result.skipped} skipped
              </p>
              {result.errors.length > 0 && (
                <ul className="mt-2 max-h-40 space-y-0.5 overflow-y-auto text-xs text-ink-600">
                  {result.errors.slice(0, 20).map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="flex justify-between gap-2">
            <Button
              onClick={() => {
                setCandidates(null);
                setResult(null);
              }}
            >
              Back to paste
            </Button>
            <div className="flex gap-2">
              {result ? (
                <Button variant="primary" onClick={onDone}>
                  Done
                </Button>
              ) : (
                <>
                  <Button onClick={onClose}>Cancel</Button>
                  <Button
                    variant="primary"
                    loading={doImport.pending}
                    disabled={picked.size === 0}
                    onClick={() => void doImport.run()}
                  >
                    Import {picked.size} selected
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function NewProspectModal({
  open,
  onClose,
  campaigns,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  campaigns: Campaign[];
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    email_status: '',
    company_name: '',
    job_title: '',
    company_industry: '',
    linkedin_url: '',
    campaign_id: '',
    deal_value: '',
    notes: '',
  });

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const create = useAction(async () => {
    await api.post('/api/prospects', {
      ...form,
      email: form.email || null,
      // Omitted for a real address: the server records it as verified.
      email_status: form.email && form.email_status ? form.email_status : undefined,
      linkedin_url: form.linkedin_url || null,
      campaign_id: form.campaign_id || null,
      deal_value: form.deal_value ? Number(form.deal_value) : null,
    });
    onDone();
  });

  return (
    <Modal open={open} onClose={onClose} title="Add a prospect">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name">
            <Input value={form.first_name} onChange={set('first_name')} />
          </Field>
          <Field label="Last name">
            <Input value={form.last_name} onChange={set('last_name')} />
          </Field>
        </div>
        <Field
          label="Email"
          hint="Optional. Mark a guessed address as unconfirmed — it stays on the record but is never emailed until you confirm it."
        >
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Input type="email" value={form.email} onChange={set('email')} />
            <Select value={form.email_status} onChange={set('email_status')} disabled={!form.email}>
              <option value="">Confirmed</option>
              <option value="inferred">Unconfirmed (a guess)</option>
            </Select>
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Company">
            <Input value={form.company_name} onChange={set('company_name')} />
          </Field>
          <Field label="Job title">
            <Input value={form.job_title} onChange={set('job_title')} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Industry">
            <Input value={form.company_industry} onChange={set('company_industry')} />
          </Field>
          <Field label="Deal value (USD)">
            <Input type="number" value={form.deal_value} onChange={set('deal_value')} />
          </Field>
        </div>
        <Field label="LinkedIn URL">
          <Input value={form.linkedin_url} onChange={set('linkedin_url')} placeholder="https://www.linkedin.com/in/…" />
        </Field>
        <Field label="Campaign">
          <Select value={form.campaign_id} onChange={set('campaign_id')}>
            <option value="">No campaign</option>
            {campaigns.map((campaign) => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Notes" hint="Anything you know that should shape the messaging.">
          <Textarea rows={3} value={form.notes} onChange={set('notes')} />
        </Field>

        {create.error && <ErrorNote message={create.error} />}

        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.pending} onClick={() => void create.run()}>
            Add prospect
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function BulkDraftModal({
  open,
  onClose,
  prospectIds,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  prospectIds: string[];
  onDone: () => void;
}) {
  const [channel, setChannel] = useState('email');
  const [instructions, setInstructions] = useState('');
  const [summary, setSummary] = useState<{ created: number; errors: Array<{ error: string }> } | null>(null);

  const run = useAction(async () => {
    const response = await api.post<{ activities: unknown[]; errors: Array<{ error: string }> }>(
      '/api/outreach/drafts/bulk',
      { prospect_ids: prospectIds, channel, instructions: instructions || undefined },
    );
    setSummary({ created: response.activities.length, errors: response.errors });
  });

  return (
    <Modal open={open} onClose={onClose} title={`Draft outreach for ${prospectIds.length} prospects`}>
      <div className="space-y-3">
        <p className="text-sm text-ink-600">
          Claude writes one message per prospect using their campaign and persona prompts. Drafts land in
          the review queue — nothing sends until you approve it.
        </p>

        <Field label="Channel">
          <Select value={channel} onChange={(event) => setChannel(event.target.value)}>
            <option value="email">Email</option>
            <option value="linkedin_dm">LinkedIn DM</option>
            <option value="linkedin_post_engage">LinkedIn post comment</option>
          </Select>
        </Field>

        <Field label="Extra instructions" hint="Applied to every draft in this batch.">
          <Textarea
            rows={3}
            value={instructions}
            onChange={(event) => setInstructions(event.target.value)}
            placeholder="Mention the Q3 GSA renewal deadline."
          />
        </Field>

        {run.error && <ErrorNote message={run.error} />}

        {summary && (
          <div className="rounded-md bg-ink-50 p-3 text-sm">
            <p className="font-medium text-ink-900">{summary.created} drafts created</p>
            {summary.errors.length > 0 && (
              <ul className="mt-2 max-h-40 space-y-0.5 overflow-y-auto text-xs text-red-700">
                {summary.errors.map((error, index) => (
                  <li key={index}>{error.error}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={summary ? onDone : onClose}>{summary ? 'Done' : 'Cancel'}</Button>
          <Button variant="primary" loading={run.pending} onClick={() => void run.run()}>
            Draft {prospectIds.length} messages
          </Button>
        </div>
      </div>
    </Modal>
  );
}
